import { ButtonProps, InputAdornment, PaperProps } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import CoreTextField, { CoreTextFieldProps } from "./TextField";
import SearchIcon from "./images/magnifying-glass.svg?react";
import ClearIcon from "./images/clear-search-icon.svg?react";
import { CoreButton, CoreIconButton } from "../button/Button";
import React, { ReactNode, useEffect, useState } from "react";
import i18nPackage from "../../i18n/i18n";
import CorePaper from "../paper";
import styles from "./SearchField.module.css";
import { ScreenReaderText } from "../../screenReader";
import { ConditionalWrapper } from "../../wrapper";

export type CoreSearchFieldProps = Omit<
	CoreTextFieldProps,
	"onChange" | "value" | "startAdornment" | "endAdornment"
> & {
	mobile?: boolean;
	/**If there is a search button, this function will be called when search button or enter is clicked. Otherwise, called on every onChange */
	onSearch: (value: string) => void;
	value: string;
	/** Id of region being updated by the search result. Usually a table. For screen reader*/
	ariaControls: string;
	/**Number of results found from search including any filters. For screen reader  */
	totalResults: number;
	/**Whether searchfield includes a search button. Default is false */
	showSearchButton?: boolean;
	/** Always shows clear button, when there is input to clear */
	showClearButton?: boolean;
	/** Renders a div instead of a form (to solve issues with nested forms) */
	noForm?: boolean;
	/**Between searchfield and search button */
	children?: ReactNode;
	searchFieldWrapperClassname?: string;
	/** Fixes issues when onSearch immediately updates something else */
	debounceSearch?: number;
	slotProps?: {
		/**Props for the search button */
		button?: Omit<ButtonProps, "onClick" | "type">;
	};
};

/**
 * Just the searchfield and maybe a button.
 *
 * For the Paper styling, wrap CoreSearchField in CoreSearchFieldPaper
 */
export default function CoreSearchField({
	mobile,
	onSearch,
	value,
	ariaControls,
	totalResults,
	showSearchButton = false,
	showClearButton,
	noForm,
	children,
	slotProps,
	searchFieldWrapperClassname,
	debounceSearch,
	...textFieldProps
}: CoreSearchFieldProps) {
	const clearButtonAriaLabel = i18nPackage.t("coreComponents.searchField.clearAriaLabel");

	const [searchText, setSearchText] = useState(value);
	const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

	// If value ever changes, sync state
	useEffect(() => {
		setSearchText(value);
	}, [value]);

	const srText =
		searchText === ""
			? i18nPackage.t("coreComponents.searchField.emptySearchSrText", { count: totalResults })
			: i18nPackage.t("coreComponents.searchField.searchSrText", {
					count: totalResults,
					searchQuery: searchText,
				});

	const { fullWidth } = textFieldProps;

	const wrapperStyle = {
		width: fullWidth ? "100%" : "auto",
	};

	return (
		<ConditionalWrapper
			condition={!noForm}
			whenTrue={(children) => (
				<form
					onSubmit={(e) => {
						e.preventDefault();
						onSearch(searchText);
					}}
					className={`${styles.searchFieldWrapper} ${searchFieldWrapperClassname ? searchFieldWrapperClassname : ""}`}
					style={wrapperStyle}
				>
					{children}
				</form>
			)}
			whenFalse={(children) => (
				<div
					className={`${styles.searchFieldWrapper} ${searchFieldWrapperClassname ? searchFieldWrapperClassname : ""}`}
					style={wrapperStyle}
				>
					{children}
				</div>
			)}
		>
			<CoreTextField
				size="small"
				{...textFieldProps}
				sx={{
					height: "100%",
					flex: "1 1 0",
					margin: 0,
					...textFieldProps.sx,
					[`&:hover .${styles.clearIcon}`]: {
						color: "#4d4d4d",
					},
				}}
				value={searchText}
				onChange={(e) => {
					const newValue = e.target.value;
					setSearchText(newValue);

					if (!showSearchButton) {
						if (debounceSearch) {
							if (debounceTimer) clearTimeout(debounceTimer);
							setDebounceTimer(setTimeout(() => onSearch(newValue), debounceSearch));
						} else {
							// In dynamic search mode, search immediately
							onSearch(newValue);
						}
					}
				}}
				{...(noForm && {
					onKeyDown: (e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							onSearch(searchText);
						}
						if (textFieldProps.onKeyDown) {
							textFieldProps.onKeyDown(e);
						}
					},
				})}
				InputProps={{
					...textFieldProps.InputProps,
					sx: {
						height: "35px",
						borderRadius: mobile ? 70 : undefined,
						...textFieldProps.InputProps?.sx,
					},
					inputProps: {
						...textFieldProps.InputProps?.inputProps,
						"aria-controls": ariaControls,
					},
					startAdornment: (
						<InputAdornment position="start">
							<SvgIcon className={styles.searchIcon}>
								<SearchIcon />
							</SvgIcon>
						</InputAdornment>
					),
					endAdornment: (showSearchButton || showClearButton) && searchText !== "" && (
						<InputAdornment position="end">
							<CoreIconButton
								onClick={() => {
									setSearchText("");
									onSearch("");
								}}
								variant="plain"
								tooltip={clearButtonAriaLabel}
								sx={{ padding: 0 }}
							>
								<SvgIcon className={styles.clearIcon}>
									<ClearIcon />
								</SvgIcon>
							</CoreIconButton>
						</InputAdornment>
					),
				}}
			/>
			<ScreenReaderText role="status" aria-live="polite">
				{searchText === value ? srText : null}
			</ScreenReaderText>
			{children}
			{showSearchButton && (
				<CoreButton
					sx={{
						height: "35px",
						width: "75px",
						minWidth: "fit-content",
						...slotProps?.button?.sx,
					}}
					{...slotProps?.button}
					{...(noForm ? { onClick: () => onSearch(searchText) } : { type: "submit" })}
				>
					{i18nPackage.t("coreComponents.searchField.searchButton")}
				</CoreButton>
			)}
		</ConditionalWrapper>
	);
}

type CoreSearchFieldPaperProps = {
	children: React.ReactNode;
	regShadow?: boolean;
} & PaperProps;

/**
 * Split CoreSearchField from paper, because some searchfields don't need the paper
 */
export function CoreSearchFieldPaper({ children, regShadow = false, ...props }: CoreSearchFieldPaperProps) {
	return (
		<CorePaper
			{...props}
			className={`${styles.corePaperWrapper} ${props.className ? props.className : ""}`}
			shadowStyle={regShadow ? "regular" : "wide"}
		>
			{children}
		</CorePaper>
	);
}
